import { useEffect, useMemo, useState, type ReactNode } from "react";
import { ru as ruDates } from "date-fns/locale";
import { Link, Outlet, useLocation } from "react-router-dom";
import {
  Activity,
  ArrowLeftRight,
  ChartNoAxesColumn,
  Clock3,
  Globe,
  ListFilter,
  PanelTop,
  Plus,
  Settings,
  Table2,
  Tag,
  Users,
} from "lucide-react";
import { useBrand, useBrandAnswer } from "@fox1ck-org/auth/saas/react";
import { useShellIdentity } from "@fox1ck-org/auth/ui";
import {
  AppShell,
  Breadcrumb,
  Button,
  ErrorBoundary,
  SegmentedControl,
  useCommandPalette,
  type AppShellLinkProps,
  type AppShellNavItem,
  type AppShellNavSection,
} from "@fox1ck-org/ui";
import { PeriodPicker, periodPresets, type PeriodValue } from "@fox1ck-org/ui/date";

import { useCrumbTailValue } from "@/lib/crumb";
import { useIsPhone } from "@/lib/hooks/use-phone";
import { useReportParams } from "@/lib/hooks/use-report-params";
import { getLocale, useT, type TKey } from "@/lib/i18n";
import { useTrackerRoles, type TrackerRoles } from "@/lib/permissions";
import { carryPeriod } from "@/lib/route-tab";
import { useGetWorkspace } from "@/lib/services/workspace-service";
import { dayIn, type ConversionBasis, type DatePreset, type Scope } from "@/lib/period";
import { PhoneMore, PhoneTitle, phoneActive, phoneNav, usePhoneSlot } from "@/shell/phone";
import { crumbsFor, placeFor, RAIL_LABEL, RAIL_PATH, type RailId } from "@/shell/places";
import { useRailCounts } from "@/shell/rail-counts";
import { SearchPalette } from "@/shell/search";
import { StatusLine, StatusLineEnd } from "@/shell/status-line";

const ICON: Record<RailId, ReactNode> = {
  today: <ChartNoAxesColumn strokeWidth={1.5} />,
  campaigns: <ListFilter strokeWidth={1.5} />,
  reports: <Table2 strokeWidth={1.5} />,
  journal: <Clock3 strokeWidth={1.5} />,
  health: <Activity strokeWidth={1.5} />,
  offers: <Tag strokeWidth={1.5} />,
  landers: <PanelTop strokeWidth={1.5} />,
  partners: <Users strokeWidth={1.5} />,
  sources: <ArrowLeftRight strokeWidth={1.5} />,
  domains: <Globe strokeWidth={1.5} />,
  settings: <Settings strokeWidth={1.5} />,
};

const WORK: RailId[] = ["today", "campaigns", "reports", "journal", "health"];
/** «Библиотека» — what a campaign is assembled from, one row each (decision 03). */
const LIBRARY: RailId[] = ["offers", "landers", "partners", "sources", "domains"];
/** The rail of a person in «своё»: his campaigns, his numbers, his offers and the library he builds from. */
export const SELF_RAIL: readonly RailId[] = ["today", "campaigns", "reports", "offers", "landers", "partners", "sources"];

/** The role a person works in, in the words the rail's user row uses. Pure. */
export function roleKey(roles: TrackerRoles): TKey {
  if (roles.isPlatformAdmin || roles.isTrackerAdmin) return "nav.role.admin";
  if (!roles.canWrite) return "nav.role.viewer";
  return "nav.role.buyer";
}

/** The router's link, as `AppShell` hands it its props. */
function RouterLink({ href, ...props }: AppShellLinkProps) {
  return <Link to={href} {...props} />;
}
const renderLink = (props: AppShellLinkProps) => <RouterLink {...props} />;

/**
 * The tracker's frame (доски Rail, RailMini, Top, Foot): `AppShell` from the
 * package with the tracker's words, counts and router. The shell holds no
 * route and no data; this file decides which rail item is lit (`places.ts`),
 * what each count is (the list RPCs' totals and GetDataHealth — nothing is
 * counted here) and which Top bar controls the screen under it obeys.
 *
 * The period and «Мои · Все» live in the URL, and every rail link carries
 * them, so the window survives moving between sections and a link opens with
 * it. The screen is the outlet; it names its own scroller (`Page`).
 *
 * At 768px and below the same shell is the phone's (доски M*): a 56px header
 * with the place's name (an object's name and the way back on its screen),
 * the period and the screen's own buttons (`PhoneActions`), and a bottom bar
 * of four places and «Ещё». The screens under it are the same screens.
 */
export function TrackerShell() {
  const t = useT();
  const { pathname, search } = useLocation();
  const roles = useTrackerRoles();
  const canWrite = roles.canWrite;
  const workspace = useGetWorkspace();
  const counts = useRailCounts();
  const palette = useCommandPalette();
  const tail = useCrumbTailValue();
  const phone = useIsPhone();
  const [more, setMore] = useState(false);
  const [phoneSlot, withPhoneSlot] = usePhoneSlot();

  const place = placeFor(pathname);
  const carried = carryPeriod(search);

  const item = (id: RailId): AppShellNavItem => {
    const count = counts[id];
    return {
      id,
      label: t(RAIL_LABEL[id]),
      icon: ICON[id],
      href: `${RAIL_PATH[id]}${carried}`,
      count: count?.value,
      countTone: count?.tone,
    };
  };
  // «своё»: only the screens of his own work (the API refuses the rest).
  const shown = (id: RailId) => !roles.isSelf || SELF_RAIL.includes(id);
  const sections: AppShellNavSection[] = [
    { id: "work", items: WORK.filter(shown).map(item) },
    { id: "library", label: t("nav.library"), items: LIBRARY.filter(shown).map(item) },
  ];

  const role = t(roleKey(roles));
  // The corners are the package's — the same OrgSwitcher (in ecosystem its
  // menu lists the org's products) and AccountMenu as in every product (П-7),
  // the product named by console's brand (spec blocks §2); the line under the name is the tracker's own role,
  // the API's answer (GetCaller).
  const shell = useShellIdentity({ caption: workspace.data?.name ? `${role} · ${workspace.data.name}` : role });
  const { mark, motif } = useBrandAnswer();
  useScreenTitle(place ? t(place.crumb) : undefined);
  const crumbs = crumbsFor(place, tail, t, carried);
  // An object's screen (a campaign, a lander) has its name and the way back;
  // a section's own tab («Журнал › Конверсии») is the section.
  const back = place?.tail && place.tail.kind !== "word" && crumbs.length > 1 ? crumbs[0]?.href : undefined;
  const phoneTitle = place ? (
    <PhoneTitle
      title={back ? crumbs[crumbs.length - 1]?.label : t(place.rail === "reports" ? "adapt.tab.report" : place.crumb)}
      back={back}
      backLabel={t("adapt.back", { place: t(place.crumb) })}
    />
  ) : undefined;

  return withPhoneSlot(
    <>
      <AppShell
        brand={shell.brand}
        primaryAction={canWrite ? { label: t("nav.newCampaign"), icon: <Plus strokeWidth={1.5} />, href: "/campaigns/new" } : undefined}
        sections={sections}
        footerItems={shown("settings") ? [item("settings")] : []}
        activeId={phone ? phoneActive(place?.rail) : place?.rail}
        renderLink={renderLink}
        locale="ru-RU"
        account={shell.account}
        labels={{
          nav: t("nav.sections"),
          collapse: t("nav.collapse"),
          expand: t("nav.expand"),
          shortcutGroup: t("nav.shortcuts"),
          mobileNav: t("nav.sections"),
        }}
        breadcrumb={
          crumbs.length > 0 ? (
            <Breadcrumb
              items={crumbs}
              label={t("nav.crumbs")}
              renderLink={(crumb, className) => (
                <Link to={crumb.href!} className={className}>
                  {crumb.label}
                </Link>
              )}
            />
          ) : null
        }
        scope={place?.scope ? <ScopeSwitch /> : undefined}
        period={place?.period ? <TopPeriod /> : undefined}
        search={{ placeholder: t("nav.search"), onOpen: () => palette.setOpen(true) }}
        statusbar={<StatusLine />}
        statusbarEnd={<StatusLineEnd />}
        mobileNav={phoneNav(t, carried, () => setMore(true))}
        mobileTitle={phoneTitle}
        mobileActions={
          <>
            {place?.period ? <TopPeriod className={PHONE_TRIGGER} /> : null}
            {phoneSlot}
          </>
        }
      >
        {/* A screen that throws gets the kit's ErrorPage on the brand's ground (board TEdges): «Повторить»
            and the way home — a full load, since the boundary keeps its error across in-app navigation. */}
        <ErrorBoundary
          locale="ru"
          mark={mark}
          motif={motif}
          action={
            <Button variant="secondary" asChild>
              <a href="/campaigns">{t("notFound.cta")}</a>
            </Button>
          }
        >
          <div className="flex h-full min-h-0 flex-col">
            <Outlet />
          </div>
        </ErrorBoundary>
      </AppShell>
      <SearchPalette open={palette.open} onOpenChange={palette.setOpen} />
      {phone ? <PhoneMore open={more} onOpenChange={setMore} carried={carried} scope={place?.scope ? <ScopeSwitch /> : undefined} /> : null}
    </>,
  );
}

/**
 * The tab: «<screen> · <brand title>» as console's brand says it — «Vibe ·
 * Трекер» in ecosystem, the product's own brand in standalone, the customer's
 * under a white-label (spec ui/brands «Product identity» — no brand string here).
 */
function useScreenTitle(screen: string | undefined) {
  const { documentTitle } = useBrand();
  const title = documentTitle(screen);
  useEffect(() => {
    if (title) document.title = title;
  }, [title]);
}

/**
 * «Мои · Все» (доска Top). Everybody in the workspace sees everything in it;
 * «Мои» is a filter by author the screen applies, not access.
 */
function ScopeSwitch() {
  const t = useT();
  const { scope, setScope } = useReportParams();
  const options: { value: Scope; label: string }[] = [
    { value: "mine", label: t("scope.mine") },
    { value: "all", label: t("scope.all") },
  ];
  return <SegmentedControl size="sm" aria-label={t("scope.label")} value={scope} options={options} onValueChange={setScope} />;
}

/** The tracker's basis ids ↔ the picker's (`by=conv` stays the address's word). */
const BASIS_TO_PICKER: Record<ConversionBasis, string> = { lead: "lead", conv: "conversion", click: "click" };
const BASIS_FROM_PICKER: Record<string, ConversionBasis> = { lead: "lead", conversion: "conv", click: "click" };

/**
 * The phone header's period (доски MReport, MCampaigns: «📅 7 дней»): the
 * same picker, its trigger without the frame — a word in the header, not a
 * field. At ≤768 the package opens it as a bottom sheet.
 */
const PHONE_TRIGGER = "h-11 border-transparent bg-transparent px-2 hover:bg-surface-2";

/**
 * The Top bar's period (доски Top, PeriodPicker): the package's PeriodPicker
 * over the app's one period in the address (`useReportParams`) — presets, a
 * two-month calendar with the comparison drawn beside it, the comparison
 * (previous · a year ago · a range of one's own · none), which date a
 * conversion counts on, and the workspace's zone in the footer. Nothing lands
 * in the address before «Применить». On a phone the same draft opens in the
 * package's bottom sheet (`sheet="auto"`, ≤768): a range of one's own and the
 * comparison there too.
 */
function TopPeriod({ className }: { className?: string }) {
  const t = useT();
  const p = useReportParams();
  const presets = useMemo(
    () =>
      periodPresets({
        today: t("period.today"),
        yesterday: t("period.yesterday"),
        "7d": t("period.7d"),
        "30d": t("period.30d"),
        mtd: t("period.mtd"),
        lastmonth: t("period.lastmonth"),
      }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [getLocale()],
  );
  const value: PeriodValue = {
    preset: p.preset,
    from: p.from,
    to: p.to,
    compare: p.compare,
    compareFrom: p.compare === "custom" ? p.compareRange?.from : undefined,
    compareTo: p.compare === "custom" ? p.compareRange?.to : undefined,
    basis: BASIS_TO_PICKER[p.basis],
  };
  return (
    <PeriodPicker
      className={className}
      sheet="auto"
      value={value}
      onChange={(v) =>
        p.setPeriod({
          preset: v.preset as DatePreset,
          from: v.from,
          to: v.to,
          compare: v.compare,
          compareFrom: v.compareFrom,
          compareTo: v.compareTo,
          basis: BASIS_FROM_PICKER[v.basis ?? "lead"] ?? "lead",
        })
      }
      presets={presets}
      today={dayIn(new Date(), p.tz)}
      compareModes={["previous", "year", "custom", "off"]}
      bases={[
        { value: "lead", label: t("period.basis.lead") },
        { value: "conversion", label: t("period.basis.conv") },
        { value: "click", label: t("period.basis.click") },
      ]}
      locale={getLocale() === "ru" ? ruDates : undefined}
      note={t("period.note", { tz: p.tz })}
      labels={{
        trigger: t("period.label"),
        custom: t("period.custom"),
        from: t("period.from"),
        to: t("period.to"),
        compare: t("period.compareWith"),
        compareModes: { previous: t("period.cmp.prev"), year: t("period.cmp.year"), custom: t("period.cmp.custom"), off: t("period.cmp.off") },
        compareFrom: t("period.cmpFrom"),
        compareTo: t("period.cmpTo"),
        basis: t("period.basis"),
        legendPeriod: t("period.legend.period"),
        legendCompare: t("period.legend.compare"),
        today: t("period.todaySuffix"),
        apply: t("common.apply"),
        cancel: t("common.cancel"),
        badge: ({ mode, days, range }) => (mode === "previous" ? t("period.badge.prev", { days }) : mode === "year" ? t("period.badge.year") : t("period.against", { range })),
      }}
    />
  );
}
